import { useContext, useState, type JSX } from "react";
import { useWindowSize } from "../../hooks/useWindowSize";
import { SetLeftSideMenuOpen, SidebarLayoutShowDesktop } from "../../layout/SideBarLayout";
import { V3IconClearFilters, V3IconFilter, V3IconMagnifier } from "../Icons";
import { ActiveIconToggle } from "../ReusableStyledComponents/ActiveFilterToggle";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";
import { V3TextInput } from "../TextInput/V3TextInput";
import { VerticalSpacing } from "../VerticalSpacing";
import {
    LCNBarWrapper,
    LCNFilterControls,
    LCNFilterIconToggle,
    LCNLabelWrapper,
    LCNSearchCreateWrapper,
    LCN_MOBILE_SEARCH_BR,
    LSPDesktopFilterPanel
} from "./ListContentNavbar.styles";
import { PageTitle } from "../ReusableStyledComponents/PageTitle";
import React from "react";
import { ManualBulkShowDesktop } from "../V3ModalUniversalAddContent/ManualBulkSelectContent.styles";
import { V3Body } from "../Typography";
import { V3TertiaryButton } from "../Button";
import { V3TertiaryCTAColor, V3TertiaryCTAIconPosition } from "../Button/V3TertiaryCTA.types";

interface ListContentNavbarProps extends React.InputHTMLAttributes<HTMLInputElement> {
    mainLabel?: string | JSX.Element;
    searchValue: string;
    subLabel?: string;
    setSearchValue?: (val: string) => void;
    isActiveSearch: boolean;
    submitForm: () => Promise<any>;
    hideFilterIcon?: boolean;
    optionalDesktopcontent?: JSX.Element;
    isActiveFilterSearch: boolean;
    onClearHandler?: () => void;
    isBulkModalVariant?: boolean;
}

export const ListContentNavbar = ({
    mainLabel,
    subLabel,
    searchValue,
    onChange,
    submitForm,
    setSearchValue,
    isActiveSearch,
    isActiveFilterSearch,
    optionalDesktopcontent,
    hideFilterIcon = false,
    onClearHandler,
    isBulkModalVariant = false
}: ListContentNavbarProps) => {
    const setLeftMenuOpen = useContext(SetLeftSideMenuOpen);
    const [searchFocused, setSearchFocused] = useState(false);

    const size = useWindowSize();
    const isMobile = (size.width && size.width < LCN_MOBILE_SEARCH_BR) || false;

    const expandedSearch = isMobile && searchFocused;

    const isLabelString = typeof mainLabel === "string";

    const TitleElemToUse = isLabelString ? PageTitle : React.Fragment;

    const RightSideWrapperToUse = isBulkModalVariant ? ManualBulkShowDesktop : React.Fragment;
    const ClearWrapper = isBulkModalVariant ? React.Fragment : SidebarLayoutShowDesktop;

    return (
        <VerticalSpacing>
            <LCNBarWrapper $searchFocused={expandedSearch} $skipWrapperPadding={isBulkModalVariant}>
                <div>
                    <LCNLabelWrapper $isLabelString={typeof mainLabel === "string"}>
                        <TitleElemToUse>{mainLabel}</TitleElemToUse>
                        {isBulkModalVariant ? (
                            <>{!!subLabel && <V3Body>{subLabel}</V3Body>}</>
                        ) : (
                            <SidebarLayoutShowDesktop>{!!subLabel && <V3BodyGrey48>{subLabel}</V3BodyGrey48>}</SidebarLayoutShowDesktop>
                        )}
                        <ClearWrapper>
                            <LSPDesktopFilterPanel>
                                {(isActiveFilterSearch || isActiveSearch) && onClearHandler && (
                                    <V3TertiaryButton
                                        iconPosition={V3TertiaryCTAIconPosition.left}
                                        icon={<V3IconClearFilters />}
                                        text="Clear Filters"
                                        onClick={onClearHandler}
                                        ctaColor={V3TertiaryCTAColor.gray}
                                    />
                                )}
                            </LSPDesktopFilterPanel>
                        </ClearWrapper>
                    </LCNLabelWrapper>
                </div>
                <RightSideWrapperToUse>
                    <LCNFilterControls $searchFocused={expandedSearch}>
                        <div>
                            {!hideFilterIcon && (
                                <LCNFilterIconToggle onClick={() => setLeftMenuOpen(true)}>
                                    <div>
                                        <ActiveIconToggle icon={<V3IconFilter />} active={isActiveFilterSearch} />
                                    </div>
                                </LCNFilterIconToggle>
                            )}
                        </div>
                        <LCNSearchCreateWrapper $searchFocused={expandedSearch}>
                            <div>{optionalDesktopcontent}</div>
                            <div>
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        submitForm();
                                        (document.activeElement as HTMLElement).blur();
                                        setSearchFocused(false);
                                    }}
                                    action="."
                                >
                                    <V3TextInput
                                        type="search"
                                        placeholder={isMobile ? "Search...." : typeof mainLabel === "string" ? `Search ${mainLabel}...` : ""}
                                        isRounded
                                        icon={
                                            <V3IconMagnifier
                                                onClick={() => {
                                                    (document.activeElement as HTMLElement).blur();
                                                    setSearchFocused(false);
                                                    submitForm();
                                                }}
                                            />
                                        }
                                        value={searchValue}
                                        onChange={onChange}
                                        name="text"
                                        autoComplete="off"
                                        onKeyDown={(e) => {
                                            if (e.code === "Enter") {
                                                (document.activeElement as HTMLElement).blur();
                                                setSearchFocused(false);
                                                submitForm();
                                            }
                                        }}
                                        onFocus={() => setSearchFocused(true)}
                                        hasClearIcon={!!searchValue.length || expandedSearch}
                                        onClearIconHandler={() => {
                                            setSearchValue && setSearchValue("");
                                            submitForm();
                                            setSearchFocused(false);
                                        }}
                                    />
                                </form>
                            </div>
                        </LCNSearchCreateWrapper>
                    </LCNFilterControls>
                </RightSideWrapperToUse>
            </LCNBarWrapper>
        </VerticalSpacing>
    );
};
